import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import Loading from 'components/Loading';
import { BudgetExpenseOrIncome } from 'business/constants';
import {
    BudgetProposalT,
    DetailedDraftOperationT,
} from 'routes/BudgetModule/config/types';
import InscriptionsGrid from 'routes/BudgetModule/components/shared/InscriptionsGrid/InscriptionsGrid';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import _ from 'lodash';
import { isEnabled, FEATURE_ORPHAN_INSCRIPTION } from 'helpers/features';
import {
    ENRICHED_OPERATION_ID,
    OPERATION_FIELD,
} from 'routes/BudgetModule/config/constants';
import { useGetProjects } from 'routes/BudgetModule/shared/useGetProjects';
import {
    getOrphanInscriptionsGridColumns,
    SECONDARY_BUDGET_AXIS_ELEMENT_COL,
} from './helpers/gridHelpers';
import { AddOrphanInscriptions } from './AddOrphanInscription';
import { addNullOperation } from './helpers/dataHelpers';
import {
    mergeDraftAndFinanceOperations,
    useGetDraftOperations,
} from '../../Operations/helpers/operationHelpers';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { BudgetProposalEdition } from '../../hooks/budgetProposalEdition';
import { useGetOperations } from '../../../hooks/operations';
import { useGetAvailableChaptersForBudgetProposal } from '../../../hooks/chapters';
import { GridReadyEvent } from 'ag-grid-community';
import { useGetAvailableNaturesForBudgetProposal } from '../../../hooks/natures';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';

const StyOrphanInscription = styled(Box, {
    name: 'StyOrphanInscription',
})({
    background: 'white',
    height: '100%',
    '& div.ag-root > div.ag-body-viewport > div.ag-center-cols-container': {
        background: 'white',
    },
    '& div.ag-root > div.ag-body-viewport div.ag-row': {
        background: 'white',
    },
    '& div.ag-root > div.ag-header > div.ag-header-viewport': {
        background: 'white',
    },
    '& .ag-theme-material': {
        minHeight: '20vh',
        background: 'white',
    },
    '& .ag-row-group': {
        display: 'flex',
        alignItems: 'center',
    },
    '& .ag-cell': {
        display: 'flex',
        alignItems: 'center',
        border: 'none',
        lineHeight: '16px',
        fontSize: '11px',
    },
    '& .ag-layout-auto-height .ag-center-cols-container': {
        minHeight: '15vh !important',
    },
});

const StyBox = styled(Box, {
    name: 'StyBox',
})({
    height: '100%',
    background: 'white',
    marginBottom: '1.5rem',
    marginTop: '1rem',
    borderRadius: '4px',
});

const StyGridHeader = styled(Box, {
    name: 'StyGridHeader',
})({
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: '24px',
    minHeight: '60px',
});

const StyGridHeaderTitle = styled(Typography, {
    name: 'StyGridHeaderTitle',
})(({ theme }) => ({
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '3px',
}));

type Props = {
    budgetProposal: BudgetProposalT;
    budgetProposals: BudgetProposalT[];
    expenseOrIncome: BudgetExpenseOrIncome;
    orphanInscriptions: InscriptionT[];
    budgetProposalEdition: BudgetProposalEdition;
    resourceSoftwareId: string;
    resourceName: string;
    inscriptionsQueryCacheKey: InscriptionQueryKey;
};

const OrphanInscriptions = ({
    budgetProposal,
    budgetProposals,
    expenseOrIncome,
    orphanInscriptions,
    budgetProposalEdition,
    resourceSoftwareId,
    resourceName,
    inscriptionsQueryCacheKey,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const integrations = useContext(IntegrationContext);

    const { data: operationsResponse, isLoading: isOperationsLoading } =
        useGetOperations(budgetProposal, resourceSoftwareId);

    const { data: chaptersResponse = [], isLoading: isChaptersLoading } =
        useGetAvailableChaptersForBudgetProposal(budgetProposal);

    const { data: naturesResponse = [], isLoading: isNaturesLoading } =
        useGetAvailableNaturesForBudgetProposal(budgetProposal);

    const { data: draftOperations, isLoading: isDraftOperationsLoading } =
        useGetDraftOperations({
            association: {
                budgetProposalId: budgetProposal.id,
                resourceSoftwareId,
                resourceBy: budgetProposal.by,
            },
        });

    const { projects } = useGetProjects();

    const onGridReady = useCallback((params: GridReadyEvent) => {
        params.api.autoSizeColumns([SECONDARY_BUDGET_AXIS_ELEMENT_COL]);
    }, []);
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    if (
        isOperationsLoading ||
        isDraftOperationsLoading ||
        !draftOperations ||
        isChaptersLoading ||
        isNaturesLoading
    ) {
        return <Loading />;
    }
    // TODO: try to remove this non-null assertion
    const operations = addNullOperation(operationsResponse!);

    const allOperations = mergeDraftAndFinanceOperations({
        operations,
        draftOperations,
    });

    // (explo-AMP Shortcut ID : 28345) momentary trick to rebuild an "inscriptionsWithMetadata"
    // sadly necessary because in some occasions, we need inscription.budgetProposalPhase
    const orphansWithMetadata = orphanInscriptions.map(
        (inscription: InscriptionT) => ({
            inscription,
        }),
    );

    // If the operation has an enriched operation but no code, it is a draft_operation.
    // We can not request draft_operation through get_data, so we rewrite the data thanks
    // to the matching and with draftOperations data
    orphansWithMetadata.forEach((orphan) => {
        const { inscription } = orphan;

        if (
            !inscription[OPERATION_FIELD] &&
            inscription[ENRICHED_OPERATION_ID]
        ) {
            const matchingDraftOperation = draftOperations.find(
                (draftOperation: DetailedDraftOperationT) =>
                    draftOperation.enrichedOperationId ===
                    inscription[ENRICHED_OPERATION_ID]?.toString(),
            );

            if (matchingDraftOperation) {
                inscription['operation.code'] = matchingDraftOperation.code;
                inscription['operation.name'] = matchingDraftOperation.name;
                inscription['operation.softwareId'] =
                    matchingDraftOperation.softwareId;
            }
        }
    });

    const canMoveOrphan = orphansWithMetadata.some(
        (orphan) => orphan.inscription.canMove,
    );

    const columns = getOrphanInscriptionsGridColumns(
        budgetProposal,
        t,
        budgetProposals,
        false,
        allOperations.length > 0,
        chaptersResponse.length > 0,
        resourceName,
        projects,
        integrations,
        sourceTable,
        inscriptionsQueryCacheKey,
    );

    const isEnabledOrphanInscription = isEnabled(FEATURE_ORPHAN_INSCRIPTION);

    return (
        <StyBox>
            <StyGridHeader>
                <StyGridHeaderTitle variant="body1SemiBold">
                    {t('budgetProposal.orphanInscriptionGridHeader')}
                </StyGridHeaderTitle>
                {canMoveOrphan && (
                    <StyGridHeaderTitle variant="body1">
                        {t('budgetProposal.orphanInscriptionGridHeaderHelper')}
                    </StyGridHeaderTitle>
                )}
            </StyGridHeader>
            <StyOrphanInscription>
                <InscriptionsGrid
                    inscriptions={orphansWithMetadata}
                    nbDisplayedInscriptions={Math.min(
                        orphansWithMetadata.length,
                        10,
                    )}
                    budgetProposals={budgetProposals}
                    expenseOrIncome={expenseOrIncome}
                    columns={columns}
                    operations={allOperations}
                    chapters={chaptersResponse}
                    natures={naturesResponse}
                    pagination
                    paginationPageSize={10}
                    queryCacheKey={inscriptionsQueryCacheKey}
                    onGridReady={onGridReady}
                />
            </StyOrphanInscription>
            {isEnabledOrphanInscription ? (
                <AddOrphanInscriptions
                    budgetProposal={budgetProposal}
                    budgetProposalEdition={budgetProposalEdition}
                    expenseOrIncome={expenseOrIncome}
                    resourceSoftwareId={resourceSoftwareId}
                    resourceName={resourceName}
                    inscriptionsQueryCacheKey={inscriptionsQueryCacheKey}
                />
            ) : null}
        </StyBox>
    );
};

const areItemEqual = (prevProps: Props, nextProps: Props) => {
    const prevOrphan = prevProps.orphanInscriptions;
    const nextOrphan = nextProps.orphanInscriptions;
    return (
        _.isEqual(prevOrphan, nextOrphan) &&
        _.isEqual(
            prevProps.budgetProposalEdition,
            nextProps.budgetProposalEdition,
        )
    );
};
export default React.memo(OrphanInscriptions, areItemEqual);
